import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { login } from "../features/auth/authSlice";
import { useNavigate, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { loginAPI } from "../services/authService";
import logo from "../assets/images/logo.png";
import logo_2 from "../assets/images/logo-3.png";
import Background1 from "../assets/images/Slider 1.jpg";
import Background2 from "../assets/images/Slider 2.jpg";
import Background3 from "../assets/images/Slider 3.jpg";
import Background4 from "../assets/images/Slider 4.jpg";
import SplitText from "../components/Animations/SplitText";
import { toast } from "react-hot-toast";
import { Eye, EyeOff } from "lucide-react";

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const defaultEmail = "admin@viswadigital.com";

  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const backgrounds = [Background1, Background2, Background3, Background4];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % backgrounds.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: defaultEmail,
      password: "",
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        emailOrUsername: data.email,
        password: data.password,
      };

      const result = await loginAPI(payload);
      console.log("Result:", result);

      if (result.status === "success") {
        setStatus("success");

        const user = result.user || {};
        const company = user.company || {};

        // Store required values in localStorage
        localStorage.setItem("token", result.token);
        localStorage.setItem("role", user.role || "");
        localStorage.setItem("companyLogo", company.logo || "");
        localStorage.setItem("companyId", company._id || "");
        localStorage.setItem("userData", JSON.stringify(user));

        dispatch(
          login({
            user: user,
            token: result.token,
          })
        );

        navigate("/fleet-overview");
      } else {
        setStatus("failed");
        toast.error(
          result.message || "Login failed. Please check your credentials."
        );
      }
    } catch (error) {
      console.error("Login error:", error);
      setStatus("error");
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const videoRef = useRef();

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.25;
    }
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="relative w-full h-screen overflow-hidden flex flex-col">
      <div className="absolute inset-0 w-full h-full z-0">
        {backgrounds.map((bg, index) => (
          <img
            key={index}
            src={bg}
            alt={`Background ${index + 1}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              index === currentBgIndex ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
      </div>

      <div className="relative flex flex-col gap-8 z-10 flex items-center justify-center w-full h-full px-4">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="relative w-full max-w-md rounded-2xl bg-white/70 shadow-xl p-10 space-y-6 text-center z-10"
        >
          <img src={logo} alt="Logo" className="mx-auto w-50 pb-2" />

          <div className="space-y-4">
            {/* Email */}
            <div className="relative">
              <input
                {...register("email", {
                  required: "Email or Username is required",
                })}
                placeholder="Email or Username"
                className="w-full px-4 py-3 rounded-full text-sm text-black placeholder:text-black/60 bg-white/90 border border-white/30 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-[#27ade3] transition-all"
              />
              {errors.email && (
                <p className="text-red-400 text-sm mt-1 text-left">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="relative">
              <input
                {...register("password", { required: "Password is required" })}
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                className="w-full px-4 py-3 pr-12 rounded-full text-sm text-black placeholder:text-black/60 bg-white/90 border border-white/30 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-[#27ade3] transition-all"
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200 focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
              {errors.password && (
                <p className="text-red-400 text-sm mt-1 text-left">
                  {errors.password.message}
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded-full bg-gradient-to-r from-[#1a325d] to-[#27ade3] 
             text-white font-semibold shadow-md hover:shadow-lg transition-all duration-300 
             border border-[#27ade3] hover:border-white/50
             hover:scale-[1.02] active:scale-95
             relative overflow-hidden group ${
               loading ? "opacity-75 cursor-not-allowed" : ""
             }`}
          >
            <span className="relative z-10">
              {loading ? "Logging in..." : "Login"}
            </span>
            <span className="absolute inset-0 bg-[#27ade3]/30 opacity-0 group-hover:opacity-100 transition duration-300 blur-sm"></span>
          </button>

          <div className="mt-2">
            <Link
              to="/forgot-password"
              className="text-lg text-[#3498db] font-medium transition duration-300 hover:text-black/70 hover:underline"
            >
              Forgot Password?
            </Link>
          </div>
        </form>

        <div className="w-full text-center z-20 overflow-hidden">
          <SplitText
            text="End to End Fuel Emission Management Solutions"
            delay={0.05}
            duration={0.6}
            ease="back.out(1.7)"
            className="text-[18px] font-bold tracking-tight font-outfit uppercase text-white drop-shadow-[0_0_6px_rgba(255,255,255,0.3)]"
          />
        </div>
        <img src={logo_2} alt="Logo" className="mx-auto w-50 pb-2" />
      </div>
    </div>
  );
}

export default Login;